// Secure API service with proper error handling and security measures
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://0oto2a7yqe.execute-api.ap-southeast-2.amazonaws.com/prod';

export interface Instance {
  InstanceId: string;
  Name: string;
  State: string;
  PublicIp?: string;
  PrivateIp?: string;
  InstanceType: string;
  LaunchTime: string;
  Owner?: string;
  StatusCheck?: string;
  KeyName?: string;
  Tags?: Array<{Key: string; Value: string}>;
  RuntimeHours?: number;  // Real-time usage from Lambda
  RuntimeSeconds?: number; // Total runtime in seconds
  QuotaHours?: number;    // Quota limit from Lambda
  LastStartTime?: number; // Unix timestamp when instance started
  Expired?: boolean;      // Quota expired flag
  AssignedStudents?: Array<{email: string; name: string}>; // List of students assigned to this instance (admin only)
}

// Sentinel message so UI code can tell an expired session apart from a real
// failure, instead of showing a misleading "please try again".
export const SESSION_EXPIRED = 'SESSION_EXPIRED';

/**
 * True if the JWT is missing, malformed, or expired.
 * Includes a 30s buffer so we never send a request that dies mid-flight.
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    // JWT payloads are base64url; normalise before decoding.
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return true;
    return payload.exp <= Math.floor(Date.now() / 1000) + 30;
  } catch {
    return true;
  }
}

/** Clear the dead session, send the user to login, and return a taggable error. */
function endSession(): Error {
  try {
    sessionStorage.clear();
  } catch {
    /* ignore storage errors */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
  return new Error(SESSION_EXPIRED);
}

class ApiService {
  private getAuthHeaders() {
    const token = sessionStorage.getItem('idToken');
    // Fail fast on an expired session rather than firing a request that will 401.
    if (isTokenExpired(token)) {
      throw endSession();
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      let errorMessage = 'Request failed';
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      switch (response.status) {
        case 401:
          // Token rejected server-side (expired or revoked between checks).
          throw endSession();
        case 403:
          throw new Error('Access denied. Insufficient permissions.');
        case 404:
          throw new Error('Resource not found.');
        case 429:
          throw new Error('Too many requests. Please try again later.');
        case 500:
        case 502:
        case 503:
        case 504:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(errorMessage);
      }
    }
    return response;
  }

  async fetchInstances(): Promise<Instance[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/instances`, {
        headers: this.getAuthHeaders(),
        signal: AbortSignal.timeout(10000)
      });
      
      await this.handleResponse(response);
      const data = await response.json();
      return Array.isArray(data.instances) ? data.instances : [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  async startInstance(instanceId: string): Promise<void> {
    if (!instanceId || !/^i-[0-9a-f]{8,17}$/.test(instanceId)) {
      throw new Error('Invalid instance ID');
    }

    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/start`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      signal: AbortSignal.timeout(30000)
    });
    
    await this.handleResponse(response);
  }

  async stopInstance(instanceId: string): Promise<void> {
    if (!instanceId || !/^i-[0-9a-f]{8,17}$/.test(instanceId)) {
      throw new Error('Invalid instance ID');
    }

    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/stop`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      signal: AbortSignal.timeout(30000)
    });
    
    await this.handleResponse(response);
  }

  async forceStopInstance(instanceId: string): Promise<void> {
    if (!instanceId || !/^i-[0-9a-f]{8,17}$/.test(instanceId)) {
      throw new Error('Invalid instance ID');
    }

    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/force-stop`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      signal: AbortSignal.timeout(30000)
    });
    
    await this.handleResponse(response);
  }



  async downloadKeypair(instanceId: string, studentEmail: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/keypair`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        action: 'download',
        instanceId,
        studentEmail
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get keypair: ${response.statusText}`);
    }
    
    return response.json();
  }

  async addIPToSecurityGroup(instanceId: string, ipAddress: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/instances/${instanceId}/add-ip`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        instanceId,
        ipAddress
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to add IP: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getMyIP(): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/my-ip`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get IP: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.ipAddress;
  }

  async getQuotaInfo(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/quota`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (response.status === 403) {
          throw new Error('Access denied. Please check your permissions.');
        }
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw new Error(`Failed to get quota info: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert seconds to hours for frontend display
      if (data.userRole === 'Student') {
        return {
          ...data,
          usedHours: (data.usedSeconds || 0) / 3600,
          totalHours: (data.quotaSeconds || 36000) / 3600,
          remainingHours: (data.remainingSeconds || 0) / 3600
        };
      }
      
      return data;
    } catch (error) {
      console.error('Failed to fetch quota info:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  async addQuota(instanceId: string, additionalHours: number): Promise<any> {
    if (!instanceId || additionalHours <= 0 || additionalHours > 1000) {
      throw new Error('Invalid parameters');
    }

    const response = await fetch(`${API_BASE_URL}/quota`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        action: 'add_quota',
        instanceId,
        additionalHours
      }),
      signal: AbortSignal.timeout(15000)
    });
    
    await this.handleResponse(response);
    return response.json();
  }

  async removeQuota(instanceId: string, hoursToRemove: number): Promise<any> {
    if (!instanceId || hoursToRemove <= 0 || hoursToRemove > 1000) {
      throw new Error('Invalid parameters');
    }

    const response = await fetch(`${API_BASE_URL}/quota`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        action: 'remove_quota',
        instanceId,
        hoursToRemove
      }),
      signal: AbortSignal.timeout(15000)
    });
    
    await this.handleResponse(response);
    return response.json();
  }

  async downloadSSHKey(instanceId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/ssh-key/${instanceId}`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get SSH key: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = data.keyName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return data;
  }

  async fetchStudents(): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/students`, {
        headers: this.getAuthHeaders(),
        signal: AbortSignal.timeout(10000)
      });
      
      await this.handleResponse(response);
      const data = await response.json();
      const students = typeof data.body === 'string' ? JSON.parse(data.body).students : data.students;
      return Array.isArray(students) ? students : [];
    } catch (error) {
      console.error('Failed to fetch students:', error);
      return [];
    }
  }
  async getShutdownLogs(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/shutdown-logs`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get shutdown logs: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching shutdown logs:', error);
      return {
        time: '12:00 AM',
        stopped_count: 0,
        duration: 'N/A',
        instances: []
      };
    }
  }
}

export const apiService = new ApiService();
